/**
 * Staff Auth Store - Pinia store for federated staff authentication
 * Handles session state for Lytning staff accessing Lytstore admin
 */

import { defineStore } from 'pinia'
import { useStaffApi } from '@/composables/useStaffApi'
import type { StaffAuthState, StaffPermission } from './types/staff.types'

/**
 * Initial state factory function
 */
function createInitialState(): StaffAuthState {
  return {
    session: null,
    initialized: false,
    loading: {
      init: false,
      logout: false
    },
    error: {
      init: '',
      logout: ''
    }
  }
}

export const useStaffAuthStore = defineStore('staffAuth', {
  state: (): StaffAuthState => createInitialState(),

  getters: {
    /**
     * Check if staff is authenticated with a valid session
     */
    isAuthenticated(state): boolean {
      return state.session !== null
    },

    /**
     * Get current store slug from session
     */
    currentSlug(state): string | null {
      return state.session?.slug ?? null
    },

    /**
     * Get current store ID from session
     */
    currentStoreId(state): string | null {
      return state.session?.storeId ?? null
    },

    /**
     * Get current store name from session
     */
    currentStoreName(state): string | null {
      return state.session?.storeName ?? null
    },

    /**
     * Get staff username
     */
    staffUsername(state): string | null {
      return state.session?.lytningUsername ?? null
    },

    /**
     * Get staff display name
     */
    staffDisplayName(state): string | null {
      return state.session?.lytningFullName || state.session?.lytningUsername || null
    },

    /**
     * Get staff role
     */
    staffRole(state): string | null {
      return state.session?.role ?? null
    },

    /**
     * Get staff permissions
     */
    staffPermissions(state): string[] {
      return state.session?.permissions ?? []
    },

    /**
     * Check if session is initialized
     */
    isInitialized(state): boolean {
      return state.initialized
    },

    /**
     * Check if currently loading
     */
    isLoading(state): boolean {
      return state.loading.init || state.loading.logout
    }
  },

  actions: {
    /**
     * Initialize session from cookie-based auth
     * Returns error code if session is invalid, null if successful
     */
    async initialize(bootstrapToken?: string): Promise<string | null> {
      const hasBootstrapToken = typeof bootstrapToken === 'string' && bootstrapToken.trim().length > 0

      // Don't reinitialize if already done (unless explicit bootstrap exchange is requested)
      if (this.initialized && !hasBootstrapToken) {
        return this.session ? null : 'NO_SESSION'
      }

      this.loading.init = true
      this.error.init = ''

      try {
        const staffApi = useStaffApi()
        const session = hasBootstrapToken
          ? await staffApi.exchangeToken(bootstrapToken!.trim())
          : await staffApi.getSession()

        if (session) {
          this.session = session
          this.initialized = true
          return null
        } else {
          this.session = null
          this.initialized = true
          return 'NO_SESSION'
        }
      } catch (error: unknown) {
        console.error('Failed to initialize staff session:', error)
        this.error.init = extractErrorMessage(error)
        this.session = null
        this.initialized = true
        return extractErrorCode(error)
      } finally {
        this.loading.init = false
      }
    },

    /**
     * Check if staff has a specific permission
     */
    hasPermission(permission: StaffPermission | string): boolean {
      return this.session?.permissions?.includes(permission) ?? false
    },

    /**
     * Check if staff has any of the specified permissions
     */
    hasAnyPermission(...permissions: (StaffPermission | string)[]): boolean {
      if (!this.session?.permissions) return false
      return permissions.some(perm => this.session!.permissions.includes(perm))
    },

    /**
     * Check if staff has all of the specified permissions
     */
    hasAllPermissions(...permissions: (StaffPermission | string)[]): boolean {
      if (!this.session?.permissions) return permissions.length === 0
      return permissions.every(perm => this.session!.permissions.includes(perm))
    },

    /**
     * Check if staff can access a specific store by slug
     */
    canAccessStore(storeSlug: string): boolean {
      return this.session?.slug === storeSlug
    },

    /**
     * Logout - terminate the current session
     */
    async logout(): Promise<void> {
      this.loading.logout = true
      this.error.logout = ''

      try {
        const staffApi = useStaffApi()
        await staffApi.logout()
      } catch (error: unknown) {
        // Best effort - continue logout even if API fails
        console.warn('Staff logout API call failed:', error)
      } finally {
        // Always clear local state
        this.session = null
        this.loading.logout = false
      }
    },

    /**
     * Reset the store to initial state
     */
    reset(): void {
      const fresh = createInitialState()
      Object.keys(this.$state).forEach((key) => {
        (this.$state as unknown as Record<string, unknown>)[key] = (fresh as unknown as Record<string, unknown>)[key]
      })
    },

    /**
     * Refresh session info (call after activity)
     */
    async refreshSession(): Promise<void> {
      if (!this.session) return

      try {
        const staffApi = useStaffApi()
        const session = await staffApi.getSession()
        if (session) {
          this.session = session
        } else {
          // Session expired
          this.session = null
        }
      } catch (error: unknown) {
        console.error('Failed to refresh staff session:', error)
        // Don't clear session on refresh failure - it might be temporary
      }
    }
  }

  // Note: We don't persist staff session state to localStorage
  // Session is maintained via HttpOnly cookie for security
})

/**
 * Extract error message from unknown error
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return 'An unexpected error occurred'
}

function extractErrorCode(error: unknown): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as Record<string, unknown>).code === 'string'
  ) {
    const code = (error as Record<string, unknown>).code
    return typeof code === 'string' ? code : 'SYSTEM_ERROR'
  }
  return 'SYSTEM_ERROR'
}
